<template>
    <div class="table-columns-menu">
        <v-menu
            v-model="menuOpen"
            offset-y
            left
            :close-on-content-click="false"
            transition="scale-transition"
            max-width="320"
            min-width="280"
            @input="onMenuToggle"
        >
            <template v-slot:activator="{ on, attrs }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on: tooltipOn, attrs: tooltipAttrs }">
                        <button
                            type="button"
                            class="table-toolbar__action"
                            :class="{
                                'table-toolbar__action--active': menuOpen,
                            }"
                            v-bind="{ ...attrs, ...tooltipAttrs }"
                            v-on="{ ...on, ...tooltipOn }"
                            :aria-label="$translate('manageColumns')"
                        >
                            <v-icon small>mdi-view-column-outline</v-icon>
                        </button>
                    </template>
                    <span>{{ $translate("manageColumns") }}</span>
                </v-tooltip>
            </template>

            <v-card class="table-columns-menu__panel">
                <div class="table-columns-menu__section-title px-4 pt-3 pb-1 text-caption grey--text text--darken-1">
                    {{ $translate("displayed") }}
                </div>
                <v-list dense class="py-0">
                    <draggable
                        v-model="localVisibleColumns"
                        handle=".table-columns-menu__drag-handle"
                        filter=".table-columns-menu__item--locked"
                        :prevent-on-filter="true"
                        :animation="150"
                        ghost-class="table-columns-menu__item--ghost"
                        chosen-class="table-columns-menu__item--chosen"
                        drag-class="table-columns-menu__item--drag"
                        :move="onColumnMove"
                        @end="onDragEnd"
                    >
                        <v-list-item
                            v-for="col in localVisibleColumns"
                            :key="'vis-' + col.key"
                            class="table-columns-menu__item"
                            :class="{
                                'table-columns-menu__item--locked': col.locked,
                            }"
                        >
                            <v-list-item-icon class="mr-2 my-0 align-self-center">
                                <v-icon v-if="col.locked" small color="grey">
                                    mdi-lock-outline
                                </v-icon>
                                <v-icon
                                    v-else
                                    small
                                    class="table-columns-menu__drag-handle"
                                >
                                    mdi-drag
                                </v-icon>
                            </v-list-item-icon>
                            <v-list-item-content>
                                <v-list-item-title>{{ col.label }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action v-if="!col.locked" class="ma-0">
                                <v-btn
                                    icon
                                    x-small
                                    :aria-label="$translate('hideColumn')"
                                    @click.stop="hideColumn(col.key)"
                                >
                                    <v-icon x-small>mdi-eye-off-outline</v-icon>
                                </v-btn>
                            </v-list-item-action>
                        </v-list-item>
                    </draggable>
                </v-list>

                <template v-if="hiddenColumns.length">
                    <v-divider class="my-2" />
                    <div class="table-columns-menu__section-title px-4 pb-1 text-caption grey--text text--darken-1">
                        {{ $translate("hidden") }}
                    </div>
                    <v-list dense class="py-0 pb-2">
                        <v-list-item
                            v-for="col in hiddenColumns"
                            :key="'hid-' + col.key"
                            @click="showColumn(col.key)"
                        >
                            <v-list-item-content>
                                <v-list-item-title>{{ col.label }}</v-list-item-title>
                            </v-list-item-content>
                            <v-list-item-action class="ma-0">
                                <v-icon small>mdi-eye-outline</v-icon>
                            </v-list-item-action>
                        </v-list-item>
                    </v-list>
                </template>

                <v-divider />
                <div class="pa-2">
                    <button
                        type="button"
                        class="table-columns-menu__reset link"
                        :disabled="!customized"
                        @click="resetColumns"
                    >
                        {{ $translate("resetColumns") }}
                    </button>
                </div>
            </v-card>
        </v-menu>
    </div>
</template>

<script>
import draggable from "vuedraggable";

export default {
    name: "TableColumnsMenu",
    components: {
        draggable,
    },
    props: {
        columns: {
            type: Array,
            default: () => [],
        },
        columnState: {
            type: Array,
            default: () => [],
        },
        customized: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            menuOpen: false,
            localVisibleColumns: [],
        };
    },
    computed: {
        visibleColumns() {
            return (this.columnState || [])
                .filter((col) => col.visible)
                .sort((a, b) => a.order - b.order);
        },
        hiddenColumns() {
            return (this.columnState || [])
                .filter((col) => !col.visible)
                .sort((a, b) => a.order - b.order);
        },
    },
    watch: {
        columnState: {
            deep: true,
            handler() {
                this.syncLocalVisibleColumns();
            },
        },
    },
    methods: {
        onMenuToggle(open) {
            if (open) {
                this.syncLocalVisibleColumns();
            }
        },
        syncLocalVisibleColumns() {
            this.localVisibleColumns = this.visibleColumns.map((col) => ({ ...col }));
        },
        emitChange(nextState) {
            this.$emit("change", nextState);
        },
        applyVisibleOrder(orderedVisible) {
            const orderMap = {};
            orderedVisible.forEach((col, index) => {
                orderMap[col.key] = index;
            });
            let hiddenOrder = orderedVisible.length;
            const next = this.columnState.map((col) => {
                if (orderMap[col.key] !== undefined) {
                    return { ...col, order: orderMap[col.key] };
                }
                if (!col.visible) {
                    const updated = { ...col, order: hiddenOrder };
                    hiddenOrder += 1;
                    return updated;
                }
                return col;
            });
            this.emitChange(next);
        },
        onDragEnd() {
            this.applyVisibleOrder(this.localVisibleColumns);
        },
        onColumnMove(evt) {
            if (evt.draggedContext?.element?.locked) {
                return false;
            }
            if (evt.relatedContext?.element?.locked) {
                return false;
            }
            return true;
        },
        hideColumn(key) {
            const target = this.columnState.find((col) => col.key === key);
            if (target?.locked) {
                return;
            }
            const next = this.columnState.map((col) =>
                col.key === key ? { ...col, visible: false } : { ...col },
            );
            this.emitChange(next);
            this.$nextTick(() => {
                this.syncLocalVisibleColumns();
            });
        },
        showColumn(key) {
            const next = this.columnState.map((col) =>
                col.key === key ? { ...col, visible: true } : { ...col },
            );
            this.emitChange(next);
            this.$nextTick(() => {
                this.syncLocalVisibleColumns();
            });
        },
        resetColumns() {
            this.$emit("reset");
        },
    },
    i18n: {
        messages: {
            en: {
                manageColumns: "Manage columns",
                displayed: "Displayed",
                hidden: "Hidden",
                hideColumn: "Hide column",
                resetColumns: "Reset to default",
            },
            fr: {
                manageColumns: "Gérer les colonnes",
                displayed: "Affichées",
                hidden: "Masquées",
                hideColumn: "Masquer la colonne",
                resetColumns: "Réinitialiser l'affichage",
            },
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/variables.scss";

.table-columns-menu__panel {
    max-height: 480px;
    overflow-y: auto;
}

.table-columns-menu__item {
    cursor: default;

    &--locked {
        opacity: 0.85;
    }

    &--ghost {
        opacity: 0.45;
        background: $color-primary-extralight;
    }

    &--chosen {
        background: $color-gs-extralight;
    }

    &--drag {
        opacity: 0.9;
        background: $color-gs-white;
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
    }
}

.table-columns-menu__drag-handle {
    cursor: grab;
    opacity: 0.55;

    &:active {
        cursor: grabbing;
    }
}

.table-columns-menu__reset {
    font-size: 13px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px 8px;
    text-decoration: underline;

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
}
</style>

<style lang="scss">
@import "@/assets/css/variables.scss";

.table-columns-menu .table-toolbar__action {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border: none;
    border-radius: 8px;
    background: transparent;
    cursor: pointer;
    color: $color-gs-extrabold;
    transition: background 0.15s ease;

    &:hover {
        background: $color-gs-extralight;
    }

    &--active {
        background: $color-gs-light;
    }
}
</style>
